import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { NoteDisclosure } from '@/components/Disclosure';
import { Hint } from '@/components/Hint';
import { StatusPill } from '@/components/Status';
import { statusPillClass } from '@/lib/status';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { OrderAction, OrderActionKind } from '@/api/orders';
import type { TodayItem } from '@/api/today';
import { AskActions } from '@/features/today/AskActions';
import { Errand } from '@/features/today/TodayItemCard';
import { chipTone } from '@/features/today/todayLib';
import { useTodayStore } from '@/stores/todayStore';
import { ACTION_KIND_META } from './actionKinds';
import {
  AcotReceivingLink, JourneyName, WorkEmpty, WorkRow, WorkSection, WorkToolbar,
} from './workLib';

/**
 * One ask. An open one carries its Today item (`item`): the same chips and
 * buttons as its Today card. An answered one shows the answer it waits on.
 */
function ActionRow({ action, item, focused, onOpenJourney, onChanged }: {
  action: OrderAction;
  item: TodayItem | undefined;
  /** The ask a link opened (`?ask=<id>`): highlighted and scrolled to. */
  focused?: boolean;
  onOpenJourney: (orderId: string) => void;
  onChanged: () => void;
}) {
  const meta = ACTION_KIND_META[action.kind] ?? ACTION_KIND_META.other;
  const answered = action.status === 'answered';

  // The why, only when it adds to the next step — and only on request.
  const why = action.nextStep && action.summary !== action.nextStep ? action.summary : null;
  const detail = (why || (answered && action.mattNote) || item) && (
    <div className="space-y-2">
      {why && <NoteDisclosure text={why} label="Why" />}
      {answered && action.mattNote && (
        <div className="flex items-start gap-1.5 text-xs">
          <StatusPill tone="info" className="shrink-0">answered</StatusPill>
          <span className="pt-0.5 text-muted-foreground">“{action.mattNote}”</span>
        </div>
      )}
      {!answered && item?.errand && <Errand errand={item.errand} />}
      {!answered && item && <AskActions item={item} onChanged={onChanged} />}
    </div>
  );

  return (
    <WorkRow
      scrollKey={`action:${action.id}`}
      title={action.vendor ? (
        <JourneyName
          vendor={action.vendor}
          label={action.label}
          onOpen={action.orderId ? () => onOpenJourney(action.orderId!) : undefined}
        />
      ) : (action.nextStep ?? action.summary)}
      detail={detail || undefined}
      className={focused ? 'bg-primary/5 ring-2 ring-inset ring-primary/40' : undefined}
    >
      <Hint content={meta.hint}>
        <span className={statusPillClass('neutral')}>{meta.label}</span>
      </Hint>
      {item?.why.map((c, i) => <span key={i} className={statusPillClass(chipTone(c))}>{c.label}</span>)}
      {action.receivingId != null && (
        <AcotReceivingLink receivingId={action.receivingId} />
      )}
      {/* NEXT STEP — the literal thing to do — is the one line in full ink. */}
      {action.vendor && <span className="text-sm">{action.nextStep ?? action.summary}</span>}
    </WorkRow>
  );
}

/**
 * The agent→Matt queue: one row per thing only Matt can do, put by the runs.
 * NEXT STEP is the row's one line; the why sits behind a toggle. Open rows
 * take Today's item for the ask — its rank, chips and buttons — so acting on
 * an ask is the same here as on Today. Answered rows wait for their run.
 */
export function ActionsView({ actions, onChanged, onOpenJourney }: {
  actions: OrderAction[];
  onChanged: () => void;
  onOpenJourney: (orderId: string) => void;
}) {
  const today = useTodayStore((s) => s.today);
  const loadToday = useTodayStore((s) => s.load);
  const [kind, setKind] = useState<OrderActionKind | 'all'>('all');
  const [vendor, setVendor] = useState<string>('all');
  // `?ask=<id>` opens one ask; `?journey=<id>` lists one journey's asks (askLinks.ts).
  const [params, setParams] = useSearchParams();
  const focusAsk = params.get('ask');
  const focusJourney = params.get('journey');
  const clearFocus = () => setParams({}, { replace: true });

  // Today's item and rank for each open ask; an ask Today lacks sorts last, oldest first.
  const byAsk = useMemo(() => {
    const m = new Map<string, { item: TodayItem; rank: number }>();
    (today?.items ?? []).forEach((item, rank) => { if (item.ask) m.set(item.ask.id, { item, rank }); });
    return m;
  }, [today]);

  const kinds = useMemo(
    () => [...new Set(actions.map((a) => a.kind))].sort((x, y) => ACTION_KIND_META[x].label.localeCompare(ACTION_KIND_META[y].label)),
    [actions]
  );
  const vendors = useMemo(
    () => [...new Set(actions.map((a) => a.vendor).filter((v): v is string => Boolean(v)))].sort((x, y) => x.localeCompare(y)),
    [actions]
  );
  const shown = useMemo(() => {
    const rank = (a: OrderAction) => byAsk.get(a.id)?.rank ?? Infinity;
    return actions
      .filter((a) => !focusJourney || a.orderId === focusJourney)
      .filter((a) => kind === 'all' || a.kind === kind)
      .filter((a) => vendor === 'all' || a.vendor === vendor)
      .sort((a, b) => rank(a) - rank(b) || a.createdAt.localeCompare(b.createdAt));
  }, [actions, kind, vendor, byAsk, focusJourney]);

  // A link to one ask clears the filters so the ask is on the list, then scrolls to it.
  useEffect(() => { if (focusAsk) { setKind('all'); setVendor('all'); } }, [focusAsk]);
  const focusShown = Boolean(focusAsk && shown.some((a) => a.id === focusAsk));
  useEffect(() => {
    if (!focusAsk || !focusShown) return;
    const frame = requestAnimationFrame(() => {
      document.querySelector(`[data-scroll-key="action:${CSS.escape(focusAsk)}"]`)?.scrollIntoView({ block: 'center' });
    });
    return () => cancelAnimationFrame(frame);
  }, [focusAsk, focusShown]);
  const focusMissing = Boolean(focusAsk && actions.length > 0 && !actions.some((a) => a.id === focusAsk));

  // A tap changes the ask on both lists.
  const changed = () => { onChanged(); void loadToday(); };

  if (actions.length === 0) {
    return <WorkEmpty>Nothing to decide.</WorkEmpty>;
  }

  const waiting = shown.filter((a) => a.status !== 'answered');
  const answered = shown.filter((a) => a.status === 'answered');

  const renderRows = (rows: OrderAction[]) => rows.map((a) => (
    <ActionRow
      key={a.id}
      action={a}
      item={a.status === 'open' ? byAsk.get(a.id)?.item : undefined}
      focused={a.id === focusAsk}
      onOpenJourney={onOpenJourney}
      onChanged={changed}
    />
  ));

  return (
    <div>
      <WorkToolbar>
        <Select value={kind} onValueChange={(v) => setKind(v as OrderActionKind | 'all')}>
          <SelectTrigger aria-label="Kind" className="w-36 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All kinds</SelectItem>
            {kinds.map((k) => <SelectItem key={k} value={k}>{ACTION_KIND_META[k].label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={vendor} onValueChange={setVendor}>
          <SelectTrigger aria-label="Vendor" className="w-48 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All vendors</SelectItem>
            {vendors.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        {(focusAsk || focusJourney) && (
          <button type="button" onClick={clearFocus} className="text-xs text-primary hover:underline">
            {focusJourney ? `One journey's asks · show all` : 'Show all'}
          </button>
        )}
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">
          {shown.length} shown
        </span>
      </WorkToolbar>
      {focusMissing && <WorkEmpty>That ask is closed.</WorkEmpty>}
      {shown.length === 0 && <WorkEmpty>Nothing matches these filters.</WorkEmpty>}
      {waiting.length > 0 && (
        <WorkSection title="Open" count={waiting.length}>
          {renderRows(waiting)}
        </WorkSection>
      )}
      {answered.length > 0 && (
        <WorkSection title="Answered" count={answered.length}>
          {renderRows(answered)}
        </WorkSection>
      )}
    </div>
  );
}
